import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, TreePine, ShieldCheck, BookOpen, Users, Sparkles, MapPin } from "lucide-react";
import { BRAND } from "../lib/brand";
import MiniLeadForm from "../components/MiniLeadForm";
import HeroSlider from "../components/HeroSlider";

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-[#EFF6FF] text-[#1D4ED8] px-3 py-1 rounded-full text-xs tracking-wide uppercase font-medium">
    <span className="h-1.5 w-1.5 rounded-full bg-[#1D4ED8]" />
    {children}
  </span>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-16 lg:pt-16 lg:pb-20 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <Pill>Clarity before capital</Pill>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-zinc-900 max-w-3xl">
              Real estate without the developer's script.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-600 max-w-xl leading-relaxed">
              Zentry helps you make better investment decisions in holiday homes across Goa &amp; Uttarakhand, and plots in India's growth corridors — through education, real data, and honest guidance.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link to="/clarity-call" className="btn-primary" data-testid="hero-book-call">
                Book a Clarity Call <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/insights" className="btn-secondary" data-testid="hero-read-insights">
                Read the basics
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <HeroSlider />
          </div>
        </div>
        <div
          aria-hidden
          className="absolute -right-40 top-10 w-[520px] h-[520px] rounded-full opacity-[0.08] blur-3xl bg-[#1D4ED8] pointer-events-none"
        />
      </section>

      {/* WHAT ZENTRY OFFERS */}
      <section className="bg-white border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <Pill>What Zentry offers</Pill>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 tracking-tight">Two markets. One approach.</h2>
            </div>
            <p className="text-zinc-600 max-w-md">
              Most investors focus on how property looks. We help you understand how it performs — and whether the numbers hold up.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/holiday-homes" className="group block bg-[#FAFAFA] rounded-3xl overflow-hidden border border-zinc-200 hover:border-[#1D4ED8] transition-all" data-testid="offer-holiday">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={BRAND.holidayImages[0]} alt="Holiday Homes" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <div className="text-xs uppercase tracking-widest text-[#1D4ED8] mb-2">Holiday Homes</div>
                <h3 className="font-display text-3xl mb-3">In Goa & Uttarakhand</h3>
                <p className="text-zinc-600">Rental income depends on occupancy, location, pricing, and management — not on brochure math.</p>
                <div className="mt-6 inline-flex items-center gap-2 text-[#1D4ED8] font-medium">Learn the basics <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
              </div>
            </Link>

            <Link to="/plots" className="group block bg-[#FAFAFA] rounded-3xl overflow-hidden border border-zinc-200 hover:border-[#1D4ED8] transition-all" data-testid="offer-plots">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={BRAND.plotsImages[0]} alt="Plots in Growth Corridors" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <div className="text-xs uppercase tracking-widest text-[#1D4ED8] mb-2">Plots & Land</div>
                <h3 className="font-display text-3xl mb-3">In Growth Corridors of India</h3>
                <p className="text-zinc-600">Land appreciation comes from infrastructure, connectivity, and demand — not hype.</p>
                <div className="mt-6 inline-flex items-center gap-2 text-[#1D4ED8] font-medium">Learn the basics <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* EXPERIENCE 3-STEP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Pill>The Zentry experience</Pill>
        <h2 className="font-display text-4xl sm:text-5xl mt-4 max-w-2xl tracking-tight">A structured way to make better real estate decisions.</h2>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            { n: "01", icon: BookOpen, title: "Learn first", text: "Understand how real estate investing actually works — from rental income to land appreciation. No selling. Just clarity." },
            { n: "02", icon: Compass, title: "Get personalised guidance", text: "Share your goals, budget, and confusion. We'll tell you what makes sense — even if that means not investing right now." },
            { n: "03", icon: Sparkles, title: "Explore the right opportunities", text: "Once your decision is clear, we introduce you to opportunities that actually fit." },
          ].map((s) => (
            <div key={s.n} className="bg-white border border-zinc-200 rounded-3xl p-8 hover:border-[#1D4ED8] hover:-translate-y-1 transition-all duration-300" data-testid={`step-${s.n}`}>
              <div className="flex items-center justify-between mb-8">
                <span className="font-display text-5xl text-zinc-200">{s.n}</span>
                <s.icon className="h-6 w-6 text-[#1D4ED8]" />
              </div>
              <h3 className="font-display text-2xl mb-3">{s.title}</h3>
              <p className="text-zinc-600">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MARKETS */}
      <section className="bg-[#1D4ED8] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <span className="inline-flex items-center gap-2 bg-white/15 text-white px-3 py-1 rounded-full text-xs tracking-wide uppercase font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-white" /> Markets we cover
              </span>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 max-w-2xl leading-tight">
                Two markets. Deeply researched. Strictly verified.
              </h2>
            </div>
            <p className="text-white/80 max-w-md leading-relaxed">
              We do not chase every new corridor or every coastal village. The markets we cover are the ones we know well enough to advise on with conviction.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-3xl p-10 hover:bg-white/15 transition-colors">
              <div className="text-xs uppercase tracking-widest text-white/70">Holiday Homes</div>
              <h3 className="font-display text-3xl mt-3">Goa &amp; Uttarakhand</h3>
              <p className="text-white/85 mt-4 leading-relaxed">
                We cover Airbnb returns, local rules, management process, return reality, occupancy, micro markets, and custom build options.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Coastal Goa — North &amp; South</li>
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Uttarakhand hill belt</li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-3xl p-10 hover:bg-white/15 transition-colors">
              <div className="text-xs uppercase tracking-widest text-white/70">Plots for Appreciation</div>
              <h3 className="font-display text-3xl mt-3">India's growth corridors</h3>
              <p className="text-white/85 mt-4 leading-relaxed">
                Led by government developments and new infrastructure. Only verified opportunities, only RERA-registered, no hype.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2"><TreePine className="h-4 w-4" /> RERA-registered developers only</li>
                <li className="flex items-center gap-2"><TreePine className="h-4 w-4" /> Infra-led, not marketing-led</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INLINE LEAD CAPTURE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <MiniLeadForm
          context="general"
          title="Not sure if real estate is right for you yet?"
          sub="Leave your number and we'll call you with an unbiased perspective on whether your goal — rental income, appreciation, or self-use — is actually achievable in today's market."
        />
      </section>

      {/* FOUNDER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-12 items-start" data-testid="founder-section">
        <div className="md:col-span-5">
          <div className="relative">
            <img src={BRAND.founder} alt="Aman Thukral, Founder of Zentry" className="rounded-3xl w-full object-cover border border-zinc-200" />
            <div className="absolute -bottom-4 -right-4 bg-white border border-zinc-200 rounded-2xl px-5 py-4 shadow-lg">
              <div className="font-display text-xl">{BRAND.founderName}</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">Founder, Zentry</div>
            </div>
          </div>
        </div>
        <div className="md:col-span-7">
          <Pill>Founder's note</Pill>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 tracking-tight leading-tight">Built because you cannot invest your hard-earned money on someone else's guesswork.</h2>
          <div className="mt-8 space-y-5 text-zinc-700 leading-relaxed">
            <p>Zentry was built by <span className="text-zinc-900 font-medium">Aman Thukral</span> who spent 10 years in advertising and events before moving into real estate. That background — designing experiences, understanding what makes people feel something when they walk into a space — came along.</p>
            <p>After years of working in real estate, a pattern became impossible to ignore. Everyone was selling the same thing — a developer's script. No research. No ground reality. Just guesswork dressed up as opportunity.</p>
            <p>Brokers who had never invested a single rupee of their own money were advising people to put crores into properties based on a presentation and a promise.</p>
            <p className="text-zinc-900 font-medium">Every piece of guidance from Zentry is backed by real market research, real data, and real experience — not a developer's sales script.</p>
          </div>
          <div className="mt-8 flex gap-3">
            <Link to="/clarity-call" className="btn-primary">Book a Clarity Call</Link>
            <Link to="/about" className="btn-secondary">Read the full story</Link>
          </div>
        </div>
      </section>

      {/* TRUST SECTION */}
      <section className="bg-[#EFF6FF]/40 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Pill>Why Zentry</Pill>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 max-w-3xl leading-tight">Most people enter real estate through marketing. We help you enter through understanding.</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, t: "We operate assets — not just sell them", d: "We've seen what works, what doesn't, and what no brochure will tell you." },
              { icon: Users, t: "We focus on outcomes, not transactions", d: "Our business isn't built on commissions. It's built on referrals that follow clarity." },
              { icon: BookOpen, t: "We tell you when not to invest", d: "That honesty is what makes our 'yes' trustworthy." },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-3xl p-8 border border-zinc-200">
                <c.icon className="h-6 w-6 text-[#1D4ED8]" />
                <div className="font-display text-2xl mt-5">{c.t}</div>
                <p className="text-zinc-600 mt-3">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1D4ED8] to-[#1E40AF] text-white p-10 md:p-16">
          <div
            aria-hidden
            className="absolute -right-24 -top-24 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl pointer-events-none"
          />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <span className="inline-flex items-center gap-2 bg-white/15 text-white px-3 py-1 rounded-full text-xs tracking-wide uppercase font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-white" /> One call. No pressure.
              </span>
              <h2 className="font-display text-4xl sm:text-5xl mt-5 leading-tight">
                Take the time to understand it properly.
              </h2>
              <p className="text-white/85 mt-5 max-w-xl leading-relaxed">
                A structured 45-minute conversation that helps you decide what makes sense — even if that means not investing right now. Bring your situation, your numbers, and any pitch you've been shown.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/clarity-call" className="inline-flex items-center gap-2 bg-white text-[#1D4ED8] hover:bg-zinc-100 px-6 py-3 rounded-full font-medium" data-testid="final-cta-book">
                  Book a Clarity Call <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/faq" className="inline-flex items-center gap-2 text-white/85 hover:text-white px-3 py-2 text-sm">
                  Read FAQs
                </Link>
              </div>
            </div>
            <div className="md:col-span-4 hidden md:block">
              <div className="space-y-3">
                {[
                  "No developer on the call",
                  "No follow-up sales pressure",
                  "Honest direction — even if it's 'don't invest now'",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 bg-white/10 border border-white/15 rounded-xl p-4">
                    <div className="h-2 w-2 rounded-full bg-white mt-2" />
                    <p className="text-sm text-white/90">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
